import React, { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import {
  ReferralPageWrapper, DentistSection, PatientSection, FormFooter, FormSection,
  SuccessScreen, RadioGroup, CheckGroup, Fw, icls,
  DENTIST_DEFAULTS, PATIENT_DEFAULTS, FOOTER_DEFAULTS,
  validateCommon, submitReferral,
} from './ReferralBase';

const CONCERN_OPTIONS = [
  { value: 'crowding', label: 'Crowding / Spaces' },
  { value: 'cosmetic', label: 'Cosmetic Anterior Alignment' },
  { value: 'class3', label: 'Class III Concern' },
  { value: 'class2', label: 'Class II Concern' },
  { value: 'deepbite', label: 'Deep Bite' },
  { value: 'openbite', label: 'Open Bite' },
  { value: 'crossbite', label: 'Cross Bite' },
  { value: 'relapse', label: 'Relapse' },
  { value: 'prerestorative', label: 'Pre-restorative Alignment' },
  { value: 'teen', label: 'Teen Orthodontics' },
];

const TREATMENT_OPTIONS = [
  { value: 'aligners', label: 'Clear Aligners' },
  { value: 'fixed', label: 'Fixed Orthodontics' },
  { value: 'either', label: 'Either / Happy to advise' },
];

const URGENCY_OPTIONS = [
  { value: 'routine', label: 'Routine' },
  { value: 'timesensitive', label: 'Time-sensitive (wedding, exams, upcoming restorative)' },
  { value: 'urgent', label: 'Urgent functional concern' },
];

const DEFAULTS = {
  ...DENTIST_DEFAULTS, ...PATIENT_DEFAULTS,
  patientType: '', reasonForReferral: '',
  primaryConcerns: [], treatmentPreference: '', urgency: '',
  specialInstructions: '',
  ...FOOTER_DEFAULTS,
};

function validate(form) {
  const errors = validateCommon(form);
  if (!form.patientType) errors.patientType = 'Please select patient type';
  if (!form.primaryConcerns.length) errors.primaryConcerns = 'Please select at least one concern';
  if (!form.treatmentPreference) errors.treatmentPreference = 'Please select a preference';
  return errors;
}

export default function OrthodonticsReferral() {
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'primaryConcerns') {
      setForm((f) => ({
        ...f,
        primaryConcerns: checked ? [...f.primaryConcerns, value] : f.primaryConcerns.filter((v) => v !== value),
      }));
      if (errors.primaryConcerns) setErrors((err) => ({ ...err, primaryConcerns: '' }));
    } else {
      setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
      if (errors[name]) setErrors((err) => ({ ...err, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try { await submitReferral({ ...form, primaryConcerns: form.primaryConcerns.join(', ') }, 'Orthodontics'); } catch (_) {}
    setSubmitting(false);
    setSubmitted(true);
  };

  const reset = () => { setForm(DEFAULTS); setErrors({}); setSubmitted(false); };
  const inputCls = (f) => icls(f, errors, focused);
  const commonProps = { form, errors, focused, onChange: handleChange, onFocus: setFocused, onBlur: () => setFocused(null) };

  return (
    <ReferralPageWrapper
      title="Orthodontics Referral"
      subtitle="Refer your patient for orthodontic assessment, clear aligners, or fixed appliance treatment."
      photo="/images/referral/referral-orthodontics.jpg">
      <h2 className="font-serif text-forest text-2xl font-semibold mb-1">Orthodontics Referral Form</h2>
      <p className="text-gray-500 text-sm mb-7">All fields marked <span className="text-red-400">*</span> are required.</p>

      <AnimatePresence mode="wait">
        {submitted ? (
          <SuccessScreen key="success" patientName={form.patientName} onReset={reset} />
        ) : (
          <form key="form" onSubmit={handleSubmit} noValidate>
            <DentistSection {...commonProps} />
            <PatientSection {...commonProps} />

            <FormSection title="Patient Type">
              <RadioGroup name="patientType" required
                options={[{ value: 'adult', label: 'Adult' }, { value: 'child', label: 'Child / Teen' }]}
                value={form.patientType} onChange={handleChange} error={errors.patientType} />
            </FormSection>

            <FormSection title="Referral Details">
              <div className="grid gap-5">
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="reasonForReferral">
                    Reason for Referral
                  </label>
                  <textarea id="reasonForReferral" name="reasonForReferral" rows={3} value={form.reasonForReferral}
                    onChange={handleChange} placeholder="Describe what the patient is hoping to achieve…"
                    onFocus={() => setFocused('reasonForReferral')} onBlur={() => setFocused(null)}
                    className={`${inputCls('reasonForReferral')} resize-none`} />
                </Fw>
                <CheckGroup name="primaryConcerns" label="Primary Concern(s)" required
                  options={CONCERN_OPTIONS} values={form.primaryConcerns}
                  onChange={handleChange} error={errors.primaryConcerns} />
                <RadioGroup name="treatmentPreference" label="Treatment Preference" required
                  options={TREATMENT_OPTIONS} value={form.treatmentPreference}
                  onChange={handleChange} error={errors.treatmentPreference} />
                <RadioGroup name="urgency" label="Time Sensitivity"
                  options={URGENCY_OPTIONS} value={form.urgency}
                  onChange={handleChange} error={errors.urgency} />
              </div>
            </FormSection>

            <FormSection title="Additional Information">
              <Fw>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="specialInstructions">
                  Special Instructions
                </label>
                <textarea id="specialInstructions" name="specialInstructions" rows={3} value={form.specialInstructions}
                  onChange={handleChange} placeholder="Any other relevant information, existing records, or radiographs to follow…"
                  onFocus={() => setFocused('specialInstructions')} onBlur={() => setFocused(null)}
                  className={`${inputCls('specialInstructions')} resize-none`} />
              </Fw>
            </FormSection>

            <FormFooter {...commonProps} submitting={submitting} />
          </form>
        )}
      </AnimatePresence>
    </ReferralPageWrapper>
  );
}
